import React, { useState, useEffect } from "react";
import api from "../../services/axiosInstance";

const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [courseSearch, setCourseSearch] = useState("");
  const studentsPerPage = 5;

  // Fetch students
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await api.get("/users/get-student");
        setStudents(res.data.professors);
      } catch (error) {
        console.error("Erreur de chargement :", error);
      }
    };
    fetchStudents();
  }, []);

  // Fetch courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await api.get("/courses/");
        setCourses(res.data);
      } catch (error) {
        console.error("Erreur fetching courses:", error);
      }
    };
    fetchCourses();
  }, []);

  // Filtered students
  const filteredStudents = students.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.email.toLowerCase().includes(search.toLowerCase()) ||
      s.wilaya.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  // Open modal
  const openModal = (studentId) => {
    setSelectedStudentId(studentId);
    setCourseSearch("");
    setModalOpen(true);
  };

  // Subscribe to course
  const handleSubscribeCourse = async (courseId) => {
    if (!courseId) return;
    try {
      await api.post("/courses/subscribed", {
        courseId,
        saved: true,
        userId: selectedStudentId,
      });
      alert("Cours ajouté avec succès !");
      setModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de l'ajout du cours :", error);
      alert("Erreur lors de l'ajout du cours");
    }
  };

  // Filtered courses in modal
  const filteredCourses = courses.filter((c) =>
    c.name.toLowerCase().includes(courseSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto bg-white p-6 rounded-2xl shadow-md">
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-4">
          <h1 className="text-3xl font-bold text-gray-800">
            Liste des étudiants
          </h1>
          <input
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-xl p-2 w-full md:w-64"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                <th className="p-3 rounded-tl-xl">Nom</th>
                <th className="p-3">Email</th>
                <th className="p-3">Wilaya</th>
                <th className="p-3">Numéro</th>
                <th className="p-3 rounded-tr-xl">Action</th>
              </tr>
            </thead>
            <tbody>
              {currentStudents.length > 0 ? (
                currentStudents.map((student) => (
                  <tr
                    key={student.id}
                    className="border-b hover:bg-gray-50 transition-all"
                  >
                    <td className="p-3">{student.name}</td>
                    <td className="p-3">{student.email}</td>
                    <td className="p-3">{student.wilaya}</td>
                    <td className="p-3">{student.number || "-"}</td>
                    <td className="p-3">
                      <button
                        onClick={() => openModal(student.id)}
                        className="px-3 py-1 bg-green-500 text-white rounded-xl hover:bg-green-600"
                      >
                        Ajouter un cours
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="p-3 text-center text-gray-500">
                    Aucun étudiant trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-6 gap-2">
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="px-4 py-2 bg-gray-200 rounded-xl disabled:opacity-50"
          >
            Précédent
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
            <button
              key={num}
              onClick={() => handlePageChange(num)}
              className={`px-4 py-2 rounded-xl ${
                num === currentPage
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {num}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            className="px-4 py-2 bg-gray-200 rounded-xl disabled:opacity-50"
          >
            Suivant
          </button>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl p-6 w-96 max-h-[80vh] overflow-y-auto relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
              onClick={() => setModalOpen(false)}
            >
              ✕
            </button>
            <h2 className="text-xl font-bold mb-4">Sélectionner un cours</h2>
            <input
              type="text"
              placeholder="Rechercher un cours..."
              value={courseSearch}
              onChange={(e) => setCourseSearch(e.target.value)}
              className="border border-gray-300 rounded-xl p-2 w-full mb-4"
            />
            <ul className="space-y-2">
              {filteredCourses.map((course) => (
                <li
                  key={course.id}
                  className="p-2 border rounded-xl hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSubscribeCourse(course.id)}
                >
                  {course.name}
                </li>
              ))}
              {filteredCourses.length === 0 && (
                <li className="text-gray-500 text-center">Aucun cours trouvé</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudents;
